import { useState, useCallback } from 'react';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { LibraryItem } from '@/contexts/UniversalLibraryContext/types';
import { updateUniversalStorageDisplayOrder } from './universal-storage-operations';
import { useDragContext } from '@/contexts/drag/DragContext';

interface UseUniversalStorageDnDProps {
  items: LibraryItem[];
  onItemsReorder: (reorderedItems: LibraryItem[]) => void;
}

/**
 * Calculate position for inserting a card
 * Exact logic: divide by 2 for first, add 1000 for last, precise midpoint for between
 */
function calculatePosition(prevOrder: number | undefined, nextOrder: number | undefined): number {
  // Moving to first position - divide the card you're replacing by 2
  if (prevOrder === undefined && nextOrder !== undefined) {
    return parseFloat((nextOrder / 2).toFixed(10));
  }
  
  // Moving to last position - add 1000 to the end card
  if (prevOrder !== undefined && nextOrder === undefined) {
    return prevOrder + 1000;
  }
  
  // Moving between cards - EXACTLY between, up to 10 decimal places
  if (prevOrder !== undefined && nextOrder !== undefined) {
    return parseFloat(((prevOrder + nextOrder) / 2).toFixed(10));
  }
  
  // Empty list fallback
  return 1000;
}

export const useUniversalStorageDnD = ({
  items,
  onItemsReorder,
}: UseUniversalStorageDnDProps) => {
  const [activeItem, setActiveItem] = useState<LibraryItem | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { setIsDragging } = useDragContext();

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setIsDragging(true);
    
    const { active } = event;
    const item = items.find(item => item.id === active.id);
    
    if (item) {
      setActiveItem(item);
    }
  }, [items, setIsDragging]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setIsDragging(false);
    setActiveItem(null);

    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // 1. Immediate UI update (optimistic) - like Content Generator pattern
    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    const movedItem = items[oldIndex];
    
    // Calculate new position between neighbors
    const prevItem = newIndex > 0 ? reorderedItems[newIndex - 1] : undefined;
    const nextItem = newIndex < reorderedItems.length - 1 ? reorderedItems[newIndex + 1] : undefined;
    
    // Get display_order from originalItem
    const prevOrder = prevItem?.originalItem?.display_order;
    const nextOrder = nextItem?.originalItem?.display_order;
    
    const newPosition = calculatePosition(prevOrder, nextOrder);

    // Update local state with new position immediately
    const updatedItems = reorderedItems.map(item => 
      item.id === movedItem.id 
        ? { 
            ...item, 
            originalItem: { 
              ...item.originalItem, 
              display_order: newPosition 
            }
          }
        : item
    );
    
    onItemsReorder(updatedItems);

    // 2. Update database silently in background (like Content Generator)
    try {
      setIsUpdating(true);
      
      await updateUniversalStorageDisplayOrder(movedItem.id, newPosition);
      
      // No cache invalidation needed - pure local state management!
      
    } catch (error) {
      console.error('[StorageDnD] Database update failed:', error);
      
      // On error, revert to original order (error recovery)
      onItemsReorder(items);
    } finally {
      setIsUpdating(false);
    }
    
  }, [items, onItemsReorder, setIsDragging]);

  return {
    activeItem,
    handleDragStart,
    handleDragEnd,
    isUpdating,
  };
};
